import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { describeError } from '@/lib/describe-error'
import {
  addCodReductionAttachments,
  approveCodReduction,
  cancelCodReduction,
  confirmCodReduction,
  createCodReduction,
  deleteCodReductionAttachment,
  findCodReductionOrders,
  getCodReductionDetail,
  getCodReductionOrderInfo,
  listCodReductionsByOrder,
  listRecentCodReductionOrders,
  previewCodReductionChain,
  rejectCodReduction,
  resubmitCodReduction,
  type CodReductionInput,
} from './cod-reductions-repository'
import type { CodReductionAttachmentKind } from '@/types/db'

export const COD_REDUCTIONS_KEY = ['cod-reductions'] as const
const detailKey = (id: number) => [...COD_REDUCTIONS_KEY, 'detail', id] as const

// Mọi thay đổi trạng thái chạm nhiều nơi (danh sách, chờ duyệt, badge, card đơn, chi tiết)
// → invalidate cả nhánh 'cod-reductions' + nhánh danh sách.
export function invalidateCodReductions(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: COD_REDUCTIONS_KEY })
  qc.invalidateQueries({ queryKey: ['cod-reductions-list'] })
}

export function useCodReductionDetail(id: number | undefined) {
  return useQuery({
    queryKey: id != null ? detailKey(id) : [...COD_REDUCTIONS_KEY, 'detail', 'none'],
    queryFn: () => getCodReductionDetail(id as number),
    enabled: typeof id === 'number' && Number.isFinite(id),
  })
}

// Card trong trang chi tiết đơn. Fail-soft: DB chưa chạy migration (bảng chưa có) thì
// trả rỗng thay vì làm hỏng cả trang đơn (3 trang deploy chung code, khác database).
export function useCodReductionsByOrder(orderId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: [...COD_REDUCTIONS_KEY, 'by-order', orderId] as const,
    queryFn: async () => {
      try {
        return await listCodReductionsByOrder(orderId as number)
      } catch (e) {
        if (isMissingRelation(e)) return []
        throw e
      }
    },
    enabled: enabled && typeof orderId === 'number' && Number.isFinite(orderId),
    retry: false,
  })
}

// PGRST205 = PostgREST chưa biết bảng; 42P01 = Postgres chưa có bảng; PGRST202 = chưa có hàm.
export function isMissingRelation(e: unknown): boolean {
  const code = (e as { code?: string } | null)?.code
  return code === 'PGRST205' || code === '42P01' || code === 'PGRST202' || code === '42883'
}

export function useCodReductionOrderInfo(orderId: number | null, enabled = true) {
  return useQuery({
    queryKey: [...COD_REDUCTIONS_KEY, 'order-info', orderId] as const,
    queryFn: () => getCodReductionOrderInfo(orderId as number),
    enabled: enabled && orderId != null,
    retry: false,
  })
}

export function useFindCodReductionOrders(query: string) {
  const q = query.trim()
  return useQuery({
    queryKey: [...COD_REDUCTIONS_KEY, 'find-orders', q] as const,
    queryFn: () => findCodReductionOrders(q),
    enabled: q.length >= 2,
    staleTime: 30_000,
  })
}

// Form tạo: 50 đơn đang giao mới nhất. FAIL-SOFT: DB chưa chạy bản migration có hàm này
// thì trả rỗng — form vẫn tìm đơn bằng ô tìm như thường.
export function useRecentCodReductionOrders(enabled = true) {
  return useQuery({
    queryKey: [...COD_REDUCTIONS_KEY, 'recent-orders'] as const,
    queryFn: async () => {
      try {
        return await listRecentCodReductionOrders(50)
      } catch (e) {
        if (isMissingRelation(e)) return []
        throw e
      }
    },
    enabled,
    staleTime: 30_000,
    retry: false,
  })
}

export function useCodReductionPreview(orderId: number | null, reduce: number, enabled: boolean) {
  return useQuery({
    queryKey: [...COD_REDUCTIONS_KEY, 'preview', orderId, reduce] as const,
    queryFn: () => previewCodReductionChain(orderId as number, reduce),
    enabled: enabled && orderId != null,
    retry: false,
  })
}

export function useCreateCodReduction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CodReductionInput) => createCodReduction(input),
    onSuccess: () => invalidateCodReductions(qc),
    onError: (e) => toast.error(describeError(e)),
  })
}
export function useResubmitCodReduction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { id: number; input: Omit<CodReductionInput, 'orderId'> }) =>
      resubmitCodReduction(args.id, args.input),
    onSuccess: () => {
      invalidateCodReductions(qc)
      toast.success('Đã gửi lại đề nghị')
    },
    onError: (e) => toast.error(describeError(e)),
  })
}
export function useApproveCodReduction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { id: number; stepId: number; comment?: string | null }) =>
      approveCodReduction(args.id, args.stepId, args.comment?.trim() || null),
    onSuccess: () => {
      invalidateCodReductions(qc)
      toast.success('Đã duyệt')
    },
    onError: (e) => {
      invalidateCodReductions(qc) // stale / đổi trạng thái → tải lại để thấy bước mới
      toast.error(describeError(e))
    },
  })
}
export function useRejectCodReduction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { id: number; stepId: number; reason: string }) =>
      rejectCodReduction(args.id, args.stepId, args.reason.trim()),
    onSuccess: () => {
      invalidateCodReductions(qc)
      toast.success('Đã từ chối — đề nghị trả về người tạo')
    },
    onError: (e) => {
      invalidateCodReductions(qc)
      toast.error(describeError(e))
    },
  })
}
export function useConfirmCodReduction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { id: number; stepId: number; note?: string | null }) =>
      confirmCodReduction(args.id, args.stepId, args.note?.trim() || null),
    onSuccess: () => {
      invalidateCodReductions(qc)
      toast.success('Đã xác nhận hạ COD')
    },
    onError: (e) => {
      invalidateCodReductions(qc)
      toast.error(describeError(e))
    },
  })
}
export function useCancelCodReduction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { id: number; reason?: string | null }) => cancelCodReduction(args.id, args.reason?.trim() || null),
    onSuccess: () => {
      invalidateCodReductions(qc)
      toast.success('Đã huỷ đề nghị')
    },
    onError: (e) => toast.error(describeError(e)),
  })
}

// Upload NHIỀU chứng từ trong 1 mutation, UI đọc `progress` ("Đang tải x/N").
export function useAddCodReductionAttachments() {
  const qc = useQueryClient()
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const mutation = useMutation({
    mutationFn: async (args: {
      requestId: number
      baseCode: string
      kind: CodReductionAttachmentKind
      files: File[]
      createdAt: string | null
      employee: string
      startSeq?: number
      onFileSaved?: (index: number) => void
    }) => {
      setProgress({ done: 0, total: args.files.length })
      try {
        return await addCodReductionAttachments(args.requestId, args.baseCode, args.kind, args.files, {
          createdAt: args.createdAt,
          employee: args.employee,
          startSeq: args.startSeq,
          onFileSaved: args.onFileSaved,
          onProgress: (done, total) => setProgress({ done, total }),
        })
      } finally {
        setProgress(null)
      }
    },
    // onSettled (không chỉ onSuccess): lỗi giữa chừng thì các file TRƯỚC đã lưu — phải tải lại
    // danh sách chứng từ, không thì số thứ tự mã file lần thử sau bị trùng.
    onSettled: () => invalidateCodReductions(qc),
    onError: (e) => toast.error(describeError(e)),
  })
  return { ...mutation, progress }
}

export function useDeleteCodReductionAttachment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (filePath: string) => deleteCodReductionAttachment(filePath),
    onSuccess: () => invalidateCodReductions(qc),
    onError: (e) => toast.error(describeError(e)),
  })
}
